import React from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { MobileNav } from "./MobileNav";
import { NotificationBell } from "./NotificationBell";
import { SignIn, UserCircle, SignOut, Plus, GearSix, ShieldCheck } from "@phosphor-icons/react";

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isWizard = location.pathname.startsWith("/dashboard/nueva") ||
                   location.pathname.startsWith("/dashboard/editar");

  return (
    <header className={`sticky top-0 z-40 bg-black/70 backdrop-blur-xl border-b border-[#C8A24A]/30 pt-safe ${isWizard ? "hidden md:block" : ""}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="ROCAFUERTE — Bienes Raíces">
          <img src="/logo.png" alt="ROCAFUERTE — Bienes Raíces" className="h-10 sm:h-12 w-auto rounded-md" />
        </Link>

        <nav className="hidden md:flex items-center gap-10 text-sm font-medium">
          <NavLink to="/" end className={({ isActive }) => isActive ? "text-[#C8A24A]" : "text-[#C8A24A]/80 hover:text-[#C8A24A]"} data-testid="nav-home">
            Inicio
          </NavLink>
          <NavLink to="/propiedades" className={({ isActive }) => isActive ? "text-[#C8A24A]" : "text-[#C8A24A]/80 hover:text-[#C8A24A]"} data-testid="nav-properties">
            Propiedades
          </NavLink>
          <NavLink to="/favoritos" className={({ isActive }) => isActive ? "text-[#C8A24A]" : "text-[#C8A24A]/80 hover:text-[#C8A24A]"} data-testid="nav-favorites">
            Favoritos
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {user && user !== false ? (
            <>
              <NotificationBell />
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard/nueva")}
                className="hidden sm:inline-flex text-[#C8A24A] hover:bg-[#C8A24A]/5"
                data-testid="header-publish-btn"
              >
                <Plus size={18} weight="bold" className="mr-1" /> Publicar
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                className="hidden sm:inline-flex"
                data-testid="header-dashboard-btn"
              >
                <UserCircle size={20} weight="duotone" className="mr-1" />
                {user.name?.split(" ")[0] || "Panel"}
              </Button>
              {user.role === "admin" && (
                <Button
                  variant="outline"
                  onClick={() => navigate("/dashboard/admin/usuarios")}
                  className="hidden md:inline-flex border-[#C8A24A]/40 text-[#C8A24A] hover:bg-[#C8A24A]/10"
                  data-testid="header-admin-btn"
                >
                  <ShieldCheck size={16} weight="fill" className="mr-1.5" /> Admin
                </Button>
              )}
              <Button variant="outline" size="icon" onClick={() => navigate("/dashboard/cuenta")} data-testid="header-account-btn" className="hidden md:inline-flex" aria-label="Configuración de cuenta">
                <GearSix size={18} />
              </Button>
              <Button variant="outline" size="icon" onClick={async () => { await logout(); navigate("/"); }} data-testid="header-logout-btn" className="hidden md:inline-flex" aria-label="Cerrar sesión">
                <SignOut size={18} />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate("/login")} className="hidden sm:inline-flex" data-testid="header-login-btn">
                <SignIn size={18} className="mr-1" /> Iniciar sesión
              </Button>
              <Button onClick={() => navigate("/registro")} className="hidden sm:inline-flex bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111]" data-testid="header-register-btn">
                Soy agente
              </Button>
            </>
          )}
          <div className="md:hidden">
            <MobileNav />
          </div>
        </div>
      </div>
    </header>
  );
};

export const Footer = () => (
  <footer className="mt-24 border-t border-[#C8A24A]/20 bg-[#0A0A0A] hidden md:block">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid gap-8 md:grid-cols-4">
      <div>
        <Link to="/" className="inline-block mb-4" aria-label="ROCAFUERTE — Bienes Raíces">
          <img src="/logo.png" alt="ROCAFUERTE — Bienes Raíces" className="h-16 w-auto rounded-md" />
        </Link>
        <p className="text-sm text-[#C8A24A]/70 leading-relaxed">
          Propiedades cuidadosamente seleccionadas en todo México. Compra, renta y publica con confianza.
        </p>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.2em] font-semibold mb-3 text-[#C8A24A]/80">Explorar</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/propiedades?operation=venta" className="hover:text-[#C8A24A]">Comprar</Link></li>
          <li><Link to="/propiedades?operation=renta" className="hover:text-[#C8A24A]">Rentar</Link></li>
          <li><Link to="/propiedades?property_type=casa" className="hover:text-[#C8A24A]">Casas</Link></li>
          <li><Link to="/propiedades?property_type=departamento" className="hover:text-[#C8A24A]">Departamentos</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.2em] font-semibold mb-3 text-[#C8A24A]/80">Agentes</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/registro" className="hover:text-[#C8A24A]">Publicar propiedad</Link></li>
          <li><Link to="/login" className="hover:text-[#C8A24A]">Iniciar sesión</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.2em] font-semibold mb-3 text-[#C8A24A]/80">Contacto</h4>
        <p className="text-sm text-[#C8A24A]/70" data-testid="footer-email">contacto@rocafuertebienesraices.com</p>
        <p className="text-sm text-[#C8A24A]/70" data-testid="footer-phone">+52 811 130 5125</p>
      </div>
    </div>
    <div className="border-t border-[#C8A24A]/20 py-6 text-center text-xs text-[#C8A24A]/60">
      © {new Date().getFullYear()} ROCAFUERTE GO. Hecho en México.
    </div>
  </footer>
);
